import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

const SENTINEL = '••••••••';

interface ConcurrencyDefaults {
  workerConcurrency: number;
  bulkConcurrency: number;
  bulkReSttConcurrency: number;
  bulkRowCeiling: number;
}

interface AiConfig {
  apiKey: string;
  baseUrl: string;
  modelStt: string;
  modelLlm: string;
  modelLlmLight: string;
  enabled: boolean;
  minDurationSeconds: number;
  dailyBudgetCents: number;
  salesPlaybook: string;
  complianceRules: string;
  skipDispositions: string[];
  workerConcurrency: number;
  bulkConcurrency: number;
  bulkReSttConcurrency: number;
  bulkRowCeiling: number;
  defaults?: ConcurrencyDefaults;
}

const FALLBACK_DEFAULTS: ConcurrencyDefaults = {
  workerConcurrency: 4,
  bulkConcurrency: 4,
  bulkReSttConcurrency: 4,
  bulkRowCeiling: 1000,
};

const EMPTY: AiConfig = {
  apiKey: '',
  baseUrl: '',
  modelStt: 'whisper-large-v3-turbo',
  modelLlm: 'openai/gpt-oss-120b',
  modelLlmLight: 'llama-3.1-8b-instant',
  enabled: false,
  minDurationSeconds: 30,
  dailyBudgetCents: 2000,
  salesPlaybook: '',
  complianceRules: '',
  skipDispositions: [],
  workerConcurrency: FALLBACK_DEFAULTS.workerConcurrency,
  bulkConcurrency: FALLBACK_DEFAULTS.bulkConcurrency,
  bulkReSttConcurrency: FALLBACK_DEFAULTS.bulkReSttConcurrency,
  bulkRowCeiling: FALLBACK_DEFAULTS.bulkRowCeiling,
  defaults: FALLBACK_DEFAULTS,
};

export function AiConfigSection(): JSX.Element {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<AiConfig>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const configQuery = useQuery({
    queryKey: ['system-config-ai'],
    queryFn: () => api.get<AiConfig>('/system-config/ai'),
  });

  useEffect(() => {
    if (configQuery.data) setForm(configQuery.data);
  }, [configQuery.data]);

  function handleString(field: keyof AiConfig) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));
  }
  function handleNumber(field: keyof AiConfig) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [field]: Number(e.target.value) || 0 }));
  }
  function handleBool(field: keyof AiConfig) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.checked }));
  }
  function handleSecretFocus() {
    if (form.apiKey === SENTINEL) setForm((f) => ({ ...f, apiKey: '' }));
  }
  function handleSecretBlur() {
    if (form.apiKey === '' && configQuery.data?.apiKey === SENTINEL) {
      setForm((f) => ({ ...f, apiKey: SENTINEL }));
    }
  }

  async function handleSave(): Promise<void> {
    setSaving(true);
    setSaveMsg(null);
    try {
      // PATCH only includes editable fields. The server skips the SENTINEL
      // placeholder so the existing key isn't overwritten when the user just
      // tweaked a model name.
      await api.patch('/system-config/ai', {
        apiKey: form.apiKey,
        baseUrl: form.baseUrl,
        modelStt: form.modelStt,
        modelLlm: form.modelLlm,
        modelLlmLight: form.modelLlmLight,
        enabled: form.enabled,
        minDurationSeconds: form.minDurationSeconds,
        dailyBudgetCents: form.dailyBudgetCents,
        salesPlaybook: form.salesPlaybook,
        complianceRules: form.complianceRules,
        skipDispositions: form.skipDispositions,
        workerConcurrency: form.workerConcurrency,
        bulkConcurrency: form.bulkConcurrency,
        bulkReSttConcurrency: form.bulkReSttConcurrency,
        bulkRowCeiling: form.bulkRowCeiling,
      });
      queryClient.invalidateQueries({ queryKey: ['system-config-ai'] });
      setSaveMsg({ ok: true, text: 'Saved successfully.' });
    } catch {
      setSaveMsg({ ok: false, text: 'Save failed. Please try again.' });
    } finally {
      setSaving(false);
    }
  }

  async function handleTest(): Promise<void> {
    setTesting(true);
    setTestMsg(null);
    try {
      const res = await api.post<{ ok: boolean; model?: string; error?: string }>(
        '/system-config/ai/test',
      );
      setTestMsg({
        ok: res.ok,
        text: res.ok ? `Connected — model ${res.model} responded.` : (res.error ?? 'Connection failed'),
      });
    } catch {
      setTestMsg({ ok: false, text: 'Request failed.' });
    } finally {
      setTesting(false);
    }
  }

  return (
    <section className="card p-4 space-y-4">
      <header className="flex items-center justify-between border-b border-ink-200 pb-3">
        <div>
          <h2 className="text-sm font-semibold text-ink-900">Audio Intelligence (Groq)</h2>
          <p className="text-xs text-ink-500 mt-0.5">
            Post-call transcription, multi-pass analysis, coaching, and optional compliance checks. Saved
            credentials take effect immediately — no restart required.
          </p>
        </div>
      </header>

      {configQuery.isLoading && <p className="text-sm text-ink-400">Loading…</p>}

      <div className="grid gap-3 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className="label">Groq API Key</label>
          <input
            className="input"
            type="password"
            value={form.apiKey}
            onChange={handleString('apiKey')}
            onFocus={handleSecretFocus}
            onBlur={handleSecretBlur}
            placeholder="gsk_..."
          />
          <p className="text-[11px] text-ink-500 mt-1">
            Get a key at console.groq.com — leave blank to run analysis in mock mode.
          </p>
        </div>

        <div>
          <label className="label">Base URL (optional)</label>
          <input
            className="input"
            value={form.baseUrl}
            onChange={handleString('baseUrl')}
            placeholder="https://api.groq.com  (leave blank for default)"
          />
        </div>
        <div>
          <label className="label">STT Model</label>
          <input
            className="input"
            value={form.modelStt}
            onChange={handleString('modelStt')}
            placeholder="whisper-large-v3-turbo"
          />
        </div>
        <div>
          <label className="label">Primary LLM Model</label>
          <input
            className="input"
            value={form.modelLlm}
            onChange={handleString('modelLlm')}
            placeholder="openai/gpt-oss-120b"
          />
        </div>
        <div>
          <label className="label">Lightweight LLM Model</label>
          <input
            className="input"
            value={form.modelLlmLight}
            onChange={handleString('modelLlmLight')}
            placeholder="llama-3.1-8b-instant"
          />
        </div>
        <div>
          <label className="label">Min Call Duration (seconds)</label>
          <input
            className="input"
            type="number"
            min={0}
            max={3600}
            value={form.minDurationSeconds}
            onChange={handleNumber('minDurationSeconds')}
          />
          <p className="text-[11px] text-ink-500 mt-1">
            Calls shorter than this are skipped (voicemails, hang-ups).
          </p>
        </div>
        <div>
          <label className="label">Daily Budget (cents)</label>
          <input
            className="input"
            type="number"
            min={0}
            max={1_000_000}
            value={form.dailyBudgetCents}
            onChange={handleNumber('dailyBudgetCents')}
          />
          <p className="text-[11px] text-ink-500 mt-1">
            Soft cap on AI spend per UTC day. Set 0 for unlimited.
          </p>
        </div>
        <div className="md:col-span-2 flex items-center gap-2 pt-1">
          <input
            id="ai-enabled"
            type="checkbox"
            checked={form.enabled}
            onChange={handleBool('enabled')}
            className="h-4 w-4"
          />
          <label htmlFor="ai-enabled" className="text-sm">
            Enable post-call AI analysis
          </label>
        </div>
        <div className="md:col-span-2">
          <label className="label">Sales Playbook</label>
          <textarea
            className="input min-h-28"
            value={form.salesPlaybook}
            onChange={handleString('salesPlaybook')}
            placeholder="Describe how agents should qualify, handle objections, and close next steps."
          />
          <p className="text-[11px] text-ink-500 mt-1">
            Used by the agent performance pass. If blank, a generic B2B sales rubric is used.
          </p>
        </div>
        <div className="md:col-span-2">
          <label className="label">Compliance Rules</label>
          <textarea
            className="input min-h-28"
            value={form.complianceRules}
            onChange={handleString('complianceRules')}
            placeholder="Leave blank to skip the compliance pass."
          />
          <p className="text-[11px] text-ink-500 mt-1">
            Leave blank to skip compliance checking for post-call analysis.
          </p>
        </div>
        <div className="md:col-span-2">
          <label className="label">Skip dispositions</label>
          <textarea
            className="input min-h-20 font-mono text-xs"
            value={form.skipDispositions.join('\n')}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                skipDispositions: e.target.value
                  .split(/[\n,]+/)
                  .map((s) => s.trim())
                  .filter(Boolean),
              }))
            }
            placeholder={'voicemail\nno_answer\nwrong_person\ndnc_request'}
          />
          <p className="text-[11px] text-ink-500 mt-1">
            Calls with one of these disposition codes skip the AI pipeline entirely (no STT, no LLM —
            <code className="px-0.5">SKIPPED_DISPOSITION</code>). Saves spend on no-contact / voicemail
            calls. One per line or comma-separated.
          </p>
        </div>
      </div>

      <div className="border-t border-ink-100 pt-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500 mb-2">
          Worker &amp; bulk-analyze
        </h3>
        <p className="text-[11px] text-ink-500 mb-2">
          Each field shows the live effective value. Type a different number to override; click "Reset" to revert to the
          server default. Worker concurrency is read fresh every tick (~5s); bulk values apply on the next bulk job.
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          {([
            { key: 'workerConcurrency' as const, label: 'Worker concurrency', help: 'Live worker — how many call analyses run in parallel.', max: 100 },
            { key: 'bulkConcurrency' as const, label: 'Bulk concurrency', help: 'Drip-feed wait threshold for bulk analyze (analyze-only mode).', max: 100 },
            { key: 'bulkReSttConcurrency' as const, label: 'Bulk re-STT concurrency', help: 'Lower cap when bulk mode forces a fresh transcript (STT cost).', max: 50 },
            { key: 'bulkRowCeiling' as const, label: 'Bulk row ceiling', help: 'Max rows per bulk job (override required to exceed).', max: 50000 },
          ]).map(({ key, label, help, max }) => {
            const defaultValue = (form.defaults ?? FALLBACK_DEFAULTS)[key];
            const isDefault = form[key] === defaultValue;
            return (
              <div key={key}>
                <div className="flex items-baseline justify-between gap-2">
                  <label className="label">{label}</label>
                  <span
                    className={`text-[10px] font-medium uppercase tracking-wide ${
                      isDefault ? 'text-ink-500' : 'text-amber-700'
                    }`}
                    title={isDefault ? 'Using server default' : 'Override active'}
                  >
                    {isDefault ? 'default' : 'overridden'}
                  </span>
                </div>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={max}
                  value={form[key]}
                  onChange={handleNumber(key)}
                />
                <div className="flex items-center justify-between gap-2 mt-1">
                  <p className="text-[11px] text-ink-500">{help}</p>
                  <button
                    type="button"
                    className="text-[10px] text-ink-500 hover:text-ink-700 underline disabled:opacity-40 disabled:no-underline"
                    onClick={() => setForm((f) => ({ ...f, [key]: defaultValue }))}
                    disabled={isDefault}
                    title={`Revert to server default (${defaultValue})`}
                  >
                    reset to {defaultValue}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button className="btn-primary" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button className="btn-secondary" onClick={() => void handleTest()} disabled={testing}>
          {testing ? 'Testing…' : 'Test Connection'}
        </button>
        {saveMsg && (
          <p className={`text-sm ${saveMsg.ok ? 'text-emerald-600' : 'text-red-600'}`}>
            {saveMsg.text}
          </p>
        )}
        {testMsg && (
          <p className={`text-sm ${testMsg.ok ? 'text-emerald-600' : 'text-red-600'}`}>
            {testMsg.text}
          </p>
        )}
      </div>
    </section>
  );
}
